import { BodyText, Flex, Heading } from '@utilitywarehouse/hearth-react';

import { FormSync } from '@features/Remote/Interaction/FormSync';
import {
  ChargeAmount,
  EnergyQuote,
  EnergyQuoteType,
  ProductType,
} from '@generated/gql/graphql';
import { UpdatedTariffModalState } from '@providers/basket/types';
import { UpdatedTariffModalVariant } from '@src/types/bundle';

import { TariffRadioGroup } from './TariffRadioGroup';

const TARIFF_TYPE_IGNORE_KEYS = ['tariffType'];

interface FormProps {
  tariffs: EnergyQuote[];
  heading: string;
  initialQuoteType?: EnergyQuoteType;
  modalState: UpdatedTariffModalState;
  selectedPrice?: Nullable<ChargeAmount>;
  onBreakdownClick: (variant: EnergyQuoteType) => void;
  isPassiveClient: boolean;
}

interface DescriptionProps {
  variant: UpdatedTariffModalVariant;
  changedServices: Nullable<ProductType[]>;
}

interface SubHeadingProps {
  variant: UpdatedTariffModalVariant;
}

const getProductLabel = (productType: Nullable<ProductType>) => {
  switch (productType) {
    case ProductType.Energy:
      return 'Energy';
    case ProductType.Broadband:
      return 'Broadband';
    case ProductType.Mobile:
      return 'Mobile';
    case ProductType.Insurance:
      return 'Insurance';
    default:
      return 'product';
  }
};

const Description = ({ variant, changedServices }: DescriptionProps) => {
  switch (variant) {
    case UpdatedTariffModalVariant.bundleExpired: {
      return (
        <BodyText data-testid="energy-tariff-modal-description">
          Before you check the changes to your bundle, please choose your energy
          tariff below.
        </BodyText>
      );
    }

    case UpdatedTariffModalVariant.productRemoved: {
      return (
        <BodyText
          weight="semibold"
          data-testid="energy-tariff-modal-description"
        >
          You removed {getProductLabel(changedServices?.[0] ?? null)} from your
          bundle, your energy tariff will be updated.
        </BodyText>
      );
    }
    default:
      return null;
  }
};

const SubHeading = ({ variant }: SubHeadingProps) => {
  switch (variant) {
    case UpdatedTariffModalVariant.initial:
    case UpdatedTariffModalVariant.priceIncreased:
    case UpdatedTariffModalVariant.priceDecreased:
    case UpdatedTariffModalVariant.bundleExpired:
    case UpdatedTariffModalVariant.productAdded: {
      return (
        <Heading as="h4" data-testid="energy-tariff-modal-sub-heading">
          Choose your energy tariff
        </Heading>
      );
    }

    default:
      return null;
  }
};

const Form = ({
  tariffs,
  heading,
  initialQuoteType,
  modalState,
  selectedPrice,
  onBreakdownClick,
  isPassiveClient,
}: FormProps) => {
  const { variant, changedServices } = modalState;

  return (
    <Flex
      direction="column"
      className="energy-tariff-modal__form-content"
      data-testid="energy-tariff-modal-content"
    >
      <Heading as="h2" size="lg" marginBottom="150">
        {heading}
      </Heading>
      <FormSync emptyFormIgnoreKeys={TARIFF_TYPE_IGNORE_KEYS} />
      <Flex
        direction="column"
        gap="250"
        className="energy-tariff-modal__tariff-tiles"
      >
        <Description variant={variant} changedServices={changedServices} />
        <Flex direction="column" gap="300">
          <SubHeading variant={variant} />
          <TariffRadioGroup
            tariffs={tariffs}
            isPassiveClient={isPassiveClient}
            selectedPrice={selectedPrice ?? null}
            initialQuoteType={initialQuoteType}
            variant={variant}
            onBreakdownClick={onBreakdownClick}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export { Form };
